/**
 * Superall i18n dictionary — bilingual (ar + en) string catalogue.
 *
 * Originally inlined as `T` in the monolithic `App.tsx`; extracted per
 * ticket superall-shell-01 so every page/component can `useT()` without
 * re-importing the monolith.
 *
 * Adding a new string: add the key under BOTH `en` and `ar` blocks. Drop-
 * outs are TS-caught because each block is typed against
 * `TranslationDict` (the `en` block is the structural truth; `ar` is
 * forced to match it).
 */

export type Lang = 'en' | 'ar';

export const SUPERALL_LANG_KEY = 'superall_lang';

const en = {
  // ── Brand / shell ────────────────────────────────────────────────
  title: 'Superall',
  tagline: 'One account. Every Othaim experience.',
  lang: 'عربي',
  beta: 'Beta',
  skipToMain: 'Skip to main content',
  settingsAria: 'Settings',

  // ── Wallet / loyalty pills ───────────────────────────────────────
  wallet: 'Wallet',
  points: 'Points',
  tier: 'Tier',
  expiring: 'expiring soon',
  walletLoading: 'Loading…',
  walletError: '—',
  notEnrolled: 'Not enrolled yet',

  // ── BU statuses ──────────────────────────────────────────────────
  live: 'متاح / Available',
  betaStatus: 'Beta',
  soon: 'قريباً / Coming Soon',
  openBU: 'Open',
  opening: 'Opening…',
  handoffError: 'Could not open — please try again',
  ourServices: 'Our services',
  walletSummary: 'Wallet and points summary',

  // ── Auth ─────────────────────────────────────────────────────────
  signIn: 'Sign in',
  signInBtn: 'Sign in',
  signingIn: 'Signing in…',
  signOut: 'Sign out',
  signOutAria: 'Sign out of account',
  redirecting: 'Redirecting…',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  emailPlaceholder: 'you@example.com',
  passwordPlaceholder: '••••••••',
  loginError: 'Invalid email or password',
  langSwitchAria: 'Switch language to Arabic',
  forgotPassword: 'Forgot password?',
  forgotPasswordTitle: 'Reset Password',
  forgotPasswordSubtitle: 'Enter your registered mobile number',
  sendOtp: 'Send OTP',
  sending: 'Sending…',
  otpSentTo: 'Code sent to',
  newPassword: 'New Password',
  confirmPassword: 'Confirm Password',
  resetPassword: 'Reset Password',
  resetting: 'Resetting…',
  resetSuccess: 'Password reset successfully',
  resetSuccessSubtitle: 'You can now sign in with your new password',
  backToSignIn: 'Back to sign in',
  changePhone: 'Change phone number',

  // ── Navigation ───────────────────────────────────────────────────
  nav: {
    home: 'Home',
    wallet: 'Wallet',
    loyalty: 'Loyalty',
    notifications: 'Notifications',
    profile: 'Profile',
    launcher: 'Launcher',
    signOut: 'Sign out',
  },

  // ── Home page ────────────────────────────────────────────────────
  home: {
    greeting: 'Welcome back',
    quickActions: 'Quick actions',
    walletTile: 'Wallet',
    walletTileSub: 'Balance and transactions',
    loyaltyTile: 'Loyalty',
    loyaltyTileSub: 'Tier and points',
    notificationsTile: 'Notifications',
    notificationsTileSub: 'Updates from your BUs',
    launcherTile: 'Launcher',
    launcherTileSub: 'Jump to any service',
  },

  // ── Placeholder coming-soon pages ────────────────────────────────
  placeholders: {
    title: 'Coming soon',
    subtitle: 'We are still building this experience. Check back shortly.',
  },

  // ── Profile menu ─────────────────────────────────────────────────
  profile: {
    menuAria: 'Open profile menu',
    viewProfile: 'View profile',
  },

  // ── Wallet page ──────────────────────────────────────────────────
  walletPage: {
    title: 'Wallet',
    balance: 'Current Balance',
    currencySuffix: 'SAR',
    transactions: 'Transactions',
    noTransactions: 'No transactions yet',
    transactionLoadError: 'Could not load transactions',
    topUp: 'Top Up',
    topUpComingSoon: 'Top-up coming soon',
    topUpComingSoonDetail:
      'Online top-up will be available shortly. Visit any O Othaim branch to top up your wallet.',
    close: 'Close',
    type_credit: 'Credit',
    type_debit: 'Debit',
    type_refund: 'Refund',
    type_topup: 'Top Up',
    type_payment: 'Payment',
    type_earn: 'Earned',
    type_redeem: 'Redeemed',
    loadMore: 'Load more',
    loading: 'Loading…',
    errorTitle: 'Could not load wallet',
    retry: 'Retry',
  },

  // ── Loyalty page ─────────────────────────────────────────────────
  loyaltyPage: {
    title: 'Loyalty',
    pointsLabel: 'Points',
    tierLabel: 'Tier',
    expirySection: 'Points Expiry',
    expiring30d: 'Expiring in 30 days',
    expiring90d: 'Expiring in 90 days',
    noExpiry: 'No points expiring soon',
    progressLabel: 'Progress to next tier',
    historyTitle: 'Points History',
    historyComingSoon: 'Detailed history coming soon',
    historyComingSoonDetail: 'View your full points history in the Loyalty app.',
    viewInLoyaltyApp: 'Open Loyalty App',
    enrolled: 'Member',
    notEnrolledTitle: 'Not yet enrolled',
    notEnrolledDetail: 'Your loyalty account will be activated on your first transaction.',
    loading: 'Loading…',
    errorTitle: 'Could not load loyalty data',
    retry: 'Retry',
    nextTierPoints: 'points to next tier',
    tierMaxReached: "You've reached the highest tier!",
  },

  // ── Notifications page ───────────────────────────────────────────
  notificationsPage: {
    title: 'Notifications',
    lastActivity: 'Last activity',
    unread: 'unread',
    unreadPlural: 'unread',
    noActivity: 'No recent activity',
    openBU: 'Open',
    emptyTitle: 'All caught up!',
    emptyDetail: 'No recent activity across your connected services.',
    loading: 'Loading…',
    errorTitle: 'Could not load activity',
    retry: 'Retry',
  },

  // ── Profile page ─────────────────────────────────────────────────
  profilePage: {
    title: 'Profile',
    accountSection: 'Account',
    namePlaceholder: 'Name not set',
    emailPlaceholder: 'Email not set',
    phonePlaceholder: 'Phone not set',
    editComingSoon: 'Profile editing coming soon',
    editComingSoonDetail: 'You will be able to update your details in a future update.',
    editProfile: 'Edit Profile',
    linkedBUsSection: 'Connected Services',
    linkedBUsDetail: 'Services you have signed into via Superall',
    openService: 'Open',
    signOutService: 'Disconnect',
    securitySection: 'Security',
    currentSession: 'Current session',
    signedInAs: 'Signed in as',
    changePasswordComingSoon: 'Password change coming soon',
    changePassword: 'Change Password',
    signOutAll: 'Sign out',
    languageSection: 'Language',
    languageAr: 'Arabic',
    languageEn: 'English',
    sessionNote: 'Full session management coming in a future update.',
    loading: 'Loading…',
    errorTitle: 'Could not load profile',
    retry: 'Retry',
  },

  // ── Launcher page ────────────────────────────────────────────────
  launcherPage: {
    title: 'Launcher',
    recentJumps: 'Recent',
    quickActions: 'Quick Actions',
    allServices: 'All Services',
    topUpWallet: 'Top Up Wallet',
    viewLoyalty: 'View Loyalty',
    viewNotifications: 'Notifications',
    noRecent: 'No recent services',
    open: 'Open',
    opening: 'Opening…',
    comingSoon: 'Coming soon',
    loadError: 'Could not load activity',
    lastVisited: 'Last visited',
  },
};

export type TranslationDict = typeof en;

/**
 * A "loose" translation dict whose leaf values are typed as `string`
 * (not the exact English string literal) while still enforcing the
 * full nested key structure.  Used for non-`en` language blocks so
 * Arabic strings aren't rejected for not matching English literals.
 */
export type LooseDict<T> = {
  [K in keyof T]: T[K] extends Record<string, unknown> ? LooseDict<T[K]> : string;
};

/** The type returned by `useT()` — structurally mirrors `en` with `string` leaves. */
export type ActiveDict = LooseDict<TranslationDict>;

const ar: ActiveDict = {
  title: 'سوبر أول',
  tagline: 'حساب واحد. كل تجارب العثيم.',
  lang: 'English',
  beta: 'تجريبي',
  skipToMain: 'تخطى إلى المحتوى الرئيسي',
  settingsAria: 'الإعدادات',

  wallet: 'المحفظة',
  points: 'النقاط',
  tier: 'المستوى',
  expiring: 'تنتهي قريباً',
  walletLoading: 'جاري التحميل…',
  walletError: '—',
  notEnrolled: 'غير مسجّل بعد',

  live: 'متاح / Available',
  betaStatus: 'تجريبي',
  soon: 'قريباً / Coming Soon',
  openBU: 'فتح',
  opening: 'جاري الفتح…',
  handoffError: 'تعذّر الفتح — حاول مرة أخرى',
  ourServices: 'خدماتنا',
  walletSummary: 'ملخص المحفظة والنقاط',

  signIn: 'تسجيل الدخول',
  signInBtn: 'دخول',
  signingIn: 'جاري الدخول…',
  signOut: 'تسجيل الخروج',
  signOutAria: 'تسجيل الخروج من الحساب',
  redirecting: 'جاري التحويل…',
  emailLabel: 'البريد الإلكتروني',
  passwordLabel: 'كلمة المرور',
  emailPlaceholder: 'name@example.com',
  passwordPlaceholder: '••••••••',
  loginError: 'بريد إلكتروني أو كلمة مرور غير صحيحة',
  langSwitchAria: 'تبديل اللغة إلى الإنجليزية',
  forgotPassword: 'نسيت كلمة المرور؟',
  forgotPasswordTitle: 'إعادة تعيين كلمة المرور',
  forgotPasswordSubtitle: 'أدخل رقم جوالك المسجّل',
  sendOtp: 'إرسال رمز التحقق',
  sending: 'جاري الإرسال…',
  otpSentTo: 'أُرسل رمز إلى',
  newPassword: 'كلمة المرور الجديدة',
  confirmPassword: 'تأكيد كلمة المرور',
  resetPassword: 'إعادة تعيين كلمة المرور',
  resetting: 'جاري إعادة التعيين…',
  resetSuccess: 'تم تغيير كلمة المرور بنجاح',
  resetSuccessSubtitle: 'يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة',
  backToSignIn: 'العودة لتسجيل الدخول',
  changePhone: 'تغيير رقم الجوال',

  nav: {
    home: 'الرئيسية',
    wallet: 'المحفظة',
    loyalty: 'الولاء',
    notifications: 'الإشعارات',
    profile: 'الحساب',
    launcher: 'الخدمات',
    signOut: 'تسجيل الخروج',
  },

  home: {
    greeting: 'مرحباً بعودتك',
    quickActions: 'إجراءات سريعة',
    walletTile: 'المحفظة',
    walletTileSub: 'الرصيد والمعاملات',
    loyaltyTile: 'الولاء',
    loyaltyTileSub: 'المستوى والنقاط',
    notificationsTile: 'الإشعارات',
    notificationsTileSub: 'تحديثات من خدماتك',
    launcherTile: 'الخدمات',
    launcherTileSub: 'انتقل إلى أي خدمة',
  },

  placeholders: {
    title: 'قريباً',
    subtitle: 'ما زلنا نبني هذه التجربة. تفقّدنا قريباً.',
  },

  profile: {
    menuAria: 'افتح قائمة الحساب',
    viewProfile: 'عرض الحساب',
  },

  walletPage: {
    title: 'المحفظة',
    balance: 'الرصيد الحالي',
    currencySuffix: 'ر.س',
    transactions: 'المعاملات',
    noTransactions: 'لا توجد معاملات بعد',
    transactionLoadError: 'تعذّر تحميل المعاملات',
    topUp: 'شحن الرصيد',
    topUpComingSoon: 'شحن الرصيد قريباً',
    topUpComingSoonDetail: 'سيتوفر الشحن الإلكتروني قريباً. قم بزيارة أي فرع العثيم لشحن محفظتك.',
    close: 'إغلاق',
    type_credit: 'إضافة',
    type_debit: 'خصم',
    type_refund: 'استرداد',
    type_topup: 'شحن',
    type_payment: 'دفع',
    type_earn: 'مكتسب',
    type_redeem: 'مستبدل',
    loadMore: 'تحميل المزيد',
    loading: 'جاري التحميل…',
    errorTitle: 'تعذّر تحميل المحفظة',
    retry: 'إعادة المحاولة',
  },

  loyaltyPage: {
    title: 'الولاء',
    pointsLabel: 'النقاط',
    tierLabel: 'المستوى',
    expirySection: 'انتهاء صلاحية النقاط',
    expiring30d: 'تنتهي خلال 30 يوماً',
    expiring90d: 'تنتهي خلال 90 يوماً',
    noExpiry: 'لا توجد نقاط تنتهي قريباً',
    progressLabel: 'التقدم نحو المستوى التالي',
    historyTitle: 'سجل النقاط',
    historyComingSoon: 'السجل التفصيلي قريباً',
    historyComingSoonDetail: 'اعرض سجل نقاطك الكامل في تطبيق الولاء.',
    viewInLoyaltyApp: 'فتح تطبيق الولاء',
    enrolled: 'عضو',
    notEnrolledTitle: 'لم يتم التسجيل بعد',
    notEnrolledDetail: 'سيتم تفعيل حساب الولاء الخاص بك عند أول معاملة.',
    loading: 'جاري التحميل…',
    errorTitle: 'تعذّر تحميل بيانات الولاء',
    retry: 'إعادة المحاولة',
    nextTierPoints: 'نقطة للمستوى التالي',
    tierMaxReached: 'لقد وصلت إلى أعلى مستوى!',
  },

  notificationsPage: {
    title: 'الإشعارات',
    lastActivity: 'آخر نشاط',
    unread: 'غير مقروء',
    unreadPlural: 'غير مقروءة',
    noActivity: 'لا يوجد نشاط مؤخراً',
    openBU: 'فتح',
    emptyTitle: 'لا توجد تحديثات!',
    emptyDetail: 'لا يوجد نشاط مؤخر في خدماتك المرتبطة.',
    loading: 'جاري التحميل…',
    errorTitle: 'تعذّر تحميل النشاط',
    retry: 'إعادة المحاولة',
  },

  profilePage: {
    title: 'الحساب',
    accountSection: 'الحساب',
    namePlaceholder: 'لم يتم تعيين الاسم',
    emailPlaceholder: 'لم يتم تعيين البريد الإلكتروني',
    phonePlaceholder: 'لم يتم تعيين رقم الهاتف',
    editComingSoon: 'تعديل الملف الشخصي قريباً',
    editComingSoonDetail: 'ستتمكن من تحديث بياناتك في تحديث قادم.',
    editProfile: 'تعديل الملف الشخصي',
    linkedBUsSection: 'الخدمات المرتبطة',
    linkedBUsDetail: 'الخدمات التي سجّلت دخولك فيها عبر سوبر أول',
    openService: 'فتح',
    signOutService: 'قطع الاتصال',
    securitySection: 'الأمان',
    currentSession: 'الجلسة الحالية',
    signedInAs: 'مسجّل الدخول بحساب',
    changePasswordComingSoon: 'تغيير كلمة المرور قريباً',
    changePassword: 'تغيير كلمة المرور',
    signOutAll: 'تسجيل الخروج',
    languageSection: 'اللغة',
    languageAr: 'العربية',
    languageEn: 'الإنجليزية',
    sessionNote: 'إدارة الجلسات الكاملة قادمة في تحديث مستقبلي.',
    loading: 'جاري التحميل…',
    errorTitle: 'تعذّر تحميل الملف الشخصي',
    retry: 'إعادة المحاولة',
  },

  launcherPage: {
    title: 'الخدمات',
    recentJumps: 'الأخيرة',
    quickActions: 'إجراءات سريعة',
    allServices: 'جميع الخدمات',
    topUpWallet: 'شحن المحفظة',
    viewLoyalty: 'عرض الولاء',
    viewNotifications: 'الإشعارات',
    noRecent: 'لا توجد خدمات مؤخراً',
    open: 'فتح',
    opening: 'جاري الفتح…',
    comingSoon: 'قريباً',
    loadError: 'تعذّر تحميل النشاط',
    lastVisited: 'آخر زيارة',
  },
};

export const STRINGS: Record<Lang, LooseDict<TranslationDict>> = { en, ar };
